import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import type { AppState, Dispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';
import { unaccentAndLower } from '@tmapy/beetlejuice-utils';

import type { MultiSelectProps } from '@tmapy/style-guide';
import {
  BtnBar,
  Form,
  FormItem,
  Input,
  MultiSelect,
  Select,
  CtaBtn,
  SecondaryBtn,
  StickyFooter,
} from '@tmapy/style-guide';

import type { OmittedFormItemProps, PublishsetFormat } from '../../types';
import { useInput, useMultiSelect } from '../../hooks';
import {
  PUBLISHSET_TYPES,
  PUBLISHSET_STATES,
  PUBLISHSET_IS_PAID,
  PUBLISHSET_ADMIN_ONLY,
} from '../constants';
import {
  publishsetMessages,
  publishsetBtnMessages,
  publishsetTypeMessages,
  publishsetStateMessages,
  publishsetIsPaidMessages,
  publishsetAdminOnlyMessages,
} from './publishsetMessages';

import { PublishsetsListQuery } from '../../openapi/operations/publishsetsList';
import { usePublishsetTagsListResource } from '../../openapi/operations/publishsetTagsList';
import { setPublishsetListFilter } from '../../redux/filter/actions';

type PublishsetFiltersProps = {
  filters: PublishsetsListQuery;
  dispatch: Dispatch;
};

export const PublishsetFilter: React.FC<PublishsetFiltersProps> = ({ filters, dispatch }) => {
  const formatMessage = useMessage();
  const hideTags = useSelector((state: AppState) => state.app.config.vydej?.hideTags);
  const isIpr = useSelector((state: AppState) => state.app.config.customerType === 'ipr');

  const name = useInput({
    name: 'name__icontains',
    initialValue: filters.name__icontains,
    label: formatMessage(publishsetMessages.name),
  });

  const typeItems = useSelectMessages(publishsetTypeMessages, PUBLISHSET_TYPES);
  const type = useInput({
    name: 'type',
    initialValue: filters.type,
    items: typeItems,
    label: formatMessage(publishsetMessages.type),
  });

  const stateItems = useSelectMessages(publishsetStateMessages, PUBLISHSET_STATES);
  const state = useInput({
    name: 'state',
    initialValue: filters.state,
    items: stateItems,
    label: formatMessage(publishsetMessages.state),
  });

  const tags = usePublishsetTagsListResource({}, true);

  const prepareTagsItems = useCallback(
    async (val: string) => {
      if (!tags.data || !tags.data.results) return null;
      return tags.data.results
        .filter((tag) => unaccentAndLower(tag.name).indexOf(unaccentAndLower(val)) >= 0)
        .map((tag) => tag.name);
    },
    [tags],
  );

  const tags__name__in = useMultiSelect<
    MultiSelectProps<PublishsetFormat> | OmittedFormItemProps,
    string
  >({
    name: 'tags__name__in',
    label: formatMessage(publishsetMessages.tags),
    initialValue: filters.tags__name__in,
    prepareItems: prepareTagsItems,
    renderValue: (item: string) => item,
    renderItem: (item: string) => item,
  });

  const creator = useInput({
    name: 'creator',
    initialValue: filters.creator,
    label: formatMessage(publishsetMessages.creator),
  });

  const editor = useInput({
    name: 'editor',
    initialValue: filters.editor,
    label: formatMessage(publishsetMessages.editor),
  });

  const isPaidItems = useSelectMessages(publishsetIsPaidMessages, PUBLISHSET_IS_PAID);
  const is_paid = useInput({
    name: 'is_paid',
    initialValue:
      filters.is_paid === true
        ? PUBLISHSET_IS_PAID.TRUE
        : filters.is_paid === false
          ? PUBLISHSET_IS_PAID.FALSE
          : undefined,
    items: isPaidItems,
    label: formatMessage(publishsetMessages.isPaid),
  });

  const adminOnlyItems = useSelectMessages(publishsetAdminOnlyMessages, PUBLISHSET_ADMIN_ONLY);
  const admin_only = useInput({
    name: 'admin_only',
    initialValue:
      filters.admin_only === true
        ? PUBLISHSET_ADMIN_ONLY.TRUE
        : filters.admin_only === false
          ? PUBLISHSET_ADMIN_ONLY.FALSE
          : undefined,
    items: adminOnlyItems,
    label: formatMessage(publishsetMessages.adminOnly),
  });

  const handleClearBtnClick = () => {
    [name, type, state, tags__name__in, creator, editor, is_paid, admin_only].forEach((formItem) =>
      formItem?.clear?.(),
    );
    dispatch(setPublishsetListFilter({}));
  };

  const handleSubmit = () => {
    dispatch(
      setPublishsetListFilter({
        [name.name!]: name.value,
        [type.name!]: type.value,
        [state.name!]: state.value,
        tags__name__in: tags__name__in.values,
        [creator.name!]: creator.value,
        [editor.name!]: editor.value,
        is_paid:
          is_paid.value === PUBLISHSET_IS_PAID.TRUE
            ? true
            : is_paid.value === PUBLISHSET_IS_PAID.FALSE
              ? false
              : undefined,
        admin_only: !isIpr
          ? undefined
          : admin_only.value === PUBLISHSET_ADMIN_ONLY.TRUE
            ? true
            : admin_only.value === PUBLISHSET_ADMIN_ONLY.FALSE
              ? false
              : undefined,
      }),
    );
  };

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-2'>
        <FormItem {...name}>
          <Input {...name} />
        </FormItem>
        {isIpr && (
          <FormItem {...admin_only}>
            <Select hasEmptyItem {...admin_only} />
          </FormItem>
        )}
        <FormItem {...type}>
          <Select hasEmptyItem {...type} />
        </FormItem>
        <FormItem {...state}>
          <Select hasEmptyItem {...state} />
        </FormItem>
        {!hideTags && (
          <FormItem {...tags__name__in}>
            <MultiSelect {...tags__name__in} />
          </FormItem>
        )}
        <FormItem {...creator}>
          <Input {...creator} />
        </FormItem>
        <FormItem {...editor}>
          <Input {...editor} />
        </FormItem>
        <FormItem {...is_paid}>
          <Select hasEmptyItem {...is_paid} />
        </FormItem>
      </div>
      <StickyFooter>
        <BtnBar stretch>
          <CtaBtn type='submit'>
            <FormattedMessage {...publishsetBtnMessages.filterSubmit} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleClearBtnClick}>
            <FormattedMessage {...publishsetBtnMessages.clear} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </Form>
  );
};
